import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';
import { notification, Checkbox, Button } from 'antd';
import Pficon from 'components/Pficon';
import { FormattedMessage, IntlProvider } from 'react-intl';
import useSessionStorage from './useSessionStorage';
import messages from './messages';

function useNotifyWithDismiss({ key, title, description, duration = 0 }) {
  const [dontShowAgain, setDontShowAgain] = useSessionStorage(key, false);
  const ref = useRef(null);

  const dismissPrevNotification = useCallback(() => {
    notification.close(key);
  }, [key]);

  const onCloseNotification = useCallback(() => {
    dismissPrevNotification();
    setDontShowAgain(ref.current.input.checked);
  }, [dismissPrevNotification, ref, setDontShowAgain]);

  const openNotification = useCallback(() => {
    if (dontShowAgain === 'true' || dontShowAgain === true) {
      return;
    }

    notification.open({
      key,
      placement: 'topRight',
      onClose: () => {
        setDontShowAgain(Boolean(ref.current?.input?.checked));
        dismissPrevNotification();
      },
      closeIcon: (
        <Pficon
          className={classNames('text-weight-500', 'margin-right-xxs')}
          type="close"
          size={16}
        />
      ),
      className: 'notification-light',
      message: (
        <div
          className={classNames(
            'padding-horizontal-md',
            'padding-top-md',
            'flex',
            'flex-items-center',
          )}
        >
          <Pficon
            type="info"
            className={classNames(
              'vertical-align-top',
              'margin-right-xxs',
              'text-size-secondary',
              'color-blue-6',
            )}
            size={18}
          />
          <span className="text-size-h3 text-weight-bold line-height-clear">
            {title}
          </span>
        </div>
      ),
      description: (
        <IntlProvider locale="en">
          <div
            className={classNames(
              'padding-horizontal-md',
              'padding-vertical-md',
            )}
          >
            {description}
            <div
              className={classNames(
                'flex',
                'flex-items-center',
                'flex-justify-between',
              )}
            >
              <Checkbox ref={ref} data-testid="notify-with-dismiss-checkbox">
                <FormattedMessage {...messages.dontShowAgain} />
              </Checkbox>

              <Button
                className={classNames(
                  'background-blue-6',
                  'background-blue-6-hover',
                  'color-white',
                )}
                onClick={onCloseNotification}
                data-testid="notify-with-dismiss-ok-btn"
              >
                <FormattedMessage {...messages.gotIt} />
              </Button>
            </div>
          </div>
        </IntlProvider>
      ),
      duration,
    });
  }, [
    dontShowAgain,
    setDontShowAgain,
    description,
    title,
    key,
    duration,
    dismissPrevNotification,
    onCloseNotification,
  ]);

  return { openNotification };
}

export default useNotifyWithDismiss;
